export type MicrophonePermissionFailureKind =
  | 'permissionDenied'
  | 'deviceUnavailable'
  | 'unknown';

interface MicrophoneTrack {
  stop: () => void;
}

interface MicrophoneStream {
  getTracks: () => MicrophoneTrack[];
}

interface MicrophoneAccessRequestOptions {
  accessAlreadyGranted: boolean;
  requestAccess: () => Promise<MicrophoneStream>;
}

const PERMISSION_DENIED_ERROR_NAMES = new Set([
  'NotAllowedError',
  'PermissionDeniedError',
  'SecurityError',
]);

const DEVICE_UNAVAILABLE_ERROR_NAMES = new Set([
  'NotFoundError',
  'DevicesNotFoundError',
  'NotReadableError',
  'TrackStartError',
  'OverconstrainedError',
]);

function getErrorName(error: unknown): string {
  if (!error || typeof error !== 'object' || !('name' in error)) return '';

  return String(error.name);
}

export function classifyMicrophonePermissionFailure(
  error: unknown,
): MicrophonePermissionFailureKind {
  const errorName = getErrorName(error);
  if (PERMISSION_DENIED_ERROR_NAMES.has(errorName)) {
    return 'permissionDenied';
  }
  if (DEVICE_UNAVAILABLE_ERROR_NAMES.has(errorName)) {
    return 'deviceUnavailable';
  }

  return 'unknown';
}

export async function requestAndReleaseMicrophoneAccess(
  requestAccess: () => Promise<MicrophoneStream>,
): Promise<void> {
  const stream = await requestAccess();
  for (const track of stream.getTracks()) track.stop();
}

export async function requestMicrophoneAccessIfNeeded({
  accessAlreadyGranted,
  requestAccess,
}: MicrophoneAccessRequestOptions): Promise<boolean> {
  if (accessAlreadyGranted) return false;

  await requestAndReleaseMicrophoneAccess(requestAccess);
  return true;
}
